import { apiClient } from '@/auth';

/**
 * Clinician-to-patient tips (short text messages). Surface in the
 * patient mobile app under App Settings → Tips. Mirrors v1's
 * `clinicianTipApi` in the generated SDK; ported here as a thin manual
 * wrapper since V2 hasn't pulled in the generated client.
 *
 * Endpoints per MNT swagger:6066 + 6100.
 *   GET  /patients/{patient_id}/clinician_tips?date_since=&date_until=
 *   POST /clinician_tips/batch?include_sms=
 */

export type ClinicianTipResponse = {
  patient_id: number;
  created_by_clinician_id: number;
  message: string;
  created_time: string;
  updated_time: string;
};

export type ClinicianTipsResponses = {
  patient_id: number;
  date_since: string;
  date_until: string;
  tips?: ClinicianTipResponse[];
};

export type CreateClinicianTipBatchRequest = {
  created_by_clinician_id: number;
  message: string;
  patient_ids: number[];
};

export const clinicianTipsApi = {
  /**
   * History for a single patient over a date range. Date params are
   * required by the server (swagger:6066) — v1 defaults to "last year"
   * which we match in the hook layer to keep this wrapper unopinionated.
   */
  async list(args: {
    patientId: number;
    dateSince: string;
    dateUntil: string;
  }): Promise<ClinicianTipsResponses> {
    const res = await apiClient.get<ClinicianTipsResponses>(
      `/patients/${args.patientId}/clinician_tips`,
      { params: { date_since: args.dateSince, date_until: args.dateUntil } },
    );
    return res.data;
  },

  /**
   * Send a tip to one or more patients. v1's UI only ever sends to one
   * patient at a time, but the endpoint is batch-shaped — we keep the
   * `patient_ids` array shape so future "send to selected patients"
   * UIs don't need an API change. `includeSms` opts the tip into the
   * SMS delivery path for SMS-enabled patients.
   */
  async sendBatch(
    body: CreateClinicianTipBatchRequest,
    opts: { includeSms?: boolean } = {},
  ): Promise<void> {
    await apiClient.post('/clinician_tips/batch', body, {
      params: opts.includeSms === undefined ? undefined : { include_sms: opts.includeSms },
    });
  },
};
